import { requestDataFromAnalyzer } from "src/shared/util/requestDatafromAnalyze"
import { OptimizationActivitiesSelected } from "src/shared/util/updateDataServer"
import { DynsCard } from "src/stateManagement/redux/slices/location/interfaces"

export interface Dyn {
    surfacePeakLoad: number
    surfaceMinLoad: number
    deltaSurfaceLoad: number
    strokeLength: number
    bottomPeakLoad: number
    bottomMinLoad: number
    deltaBottomLoad: number
    surfaceUnitBrand: string
    surfaceUnitModel: string
    structuralMaxLoad: number
    currentPercentageMaxLoad: number
    mpt: number
    ept: number
    datasetSurface: any
    datasetBottom: any
}

export interface DynSort {
    fieldActivityId: string
    executedDate: string
    spm: number
    pumpEfficiency: number
    dpluger: any
    bbpd: number
    adjustProduction: number
    variables: any[]
    dyns: Dyn[],
    diagnostic: string,
    recommendations: string
    candidateSate: string
    bottomHoleConditions: string
    subBottomHoleConditions: string
    optimizationActivitiesSelected: OptimizationActivitiesSelected[];
}

export const dpiston = [
    { label: '0.875', id: 0.875 },
    { label: '1', id: 1 },
    { label: '1.25', id: 1.25 },
    { label: '1.5', id: 1.5 },
    { label: '1.75', id: 1.75 },
    { label: '2', id: 2 },
    { label: '2.25', id: 2.25 },
    { label: '2.5', id: 2.5 },
    { label: '2.75', id: 2.75 },
    { label: '3.75', id: 3.75 },
    { label: '4.75', id: 4.75 }
];

export const analyzeAndSortDyn = async (dyn: any, wellboreDiagram: any[]): Promise<DynSort> => {

    const variables = dyn['activityVariables']

    const previousDiagnostic = dyn['diagnosticVariables']

    const cards = dyn['cardData']

    const executedDate = dyn?.executedDate
    const fieldActivityId = dyn.fieldActivityId

    let spm = variables?.find((v: any) => v.variable === "SPM")?.value

    const lastWellboreSchematic = wellboreDiagram?.[0]

    if (typeof spm !== 'number') {
        spm = parseFloat(spm);
    }

    let thp = variables?.find((v: any) => v.variable === "THP")?.value

    if (typeof thp !== 'number') {
        thp = parseFloat(thp);
    }

    let pot = variables?.find((v: any) => v.variable === "POT")?.value

    if (typeof pot !== 'number') {
        pot = parseFloat(pot);
    }

    let pumpEfficiency = previousDiagnostic?.find((v: any) => v.id === "81047dd8-05d4-11f0-af91-00505652cca1")?.value || 100

    if (typeof pumpEfficiency !== 'number') {
        pumpEfficiency = parseFloat(pumpEfficiency);
    }

    let ept = previousDiagnostic?.find((v: any) => v.id === "bca80cfd-fcdd-11ef-a39b-00505652cca1")?.value

    if (ept && typeof ept !== 'number') {
        ept = parseFloat(ept);
    }

    let dpluger = previousDiagnostic?.find((v: any) => v.id === "e47fc3c4-fc6f-11ef-a39b-00505652cca1")?.value

    if (!dpluger) {
       const schematicPlunerDiameter = lastWellboreSchematic?.plungerDiameter || 0;
       if (schematicPlunerDiameter && schematicPlunerDiameter !== ''){
            dpluger = schematicPlunerDiameter;
       }
    }

    if (typeof dpluger !== 'number') {
        dpluger = parseFloat(dpluger);
    }

    if (!dpiston.find((v: any) => v.id === dpluger)) {
        dpluger = dpiston[0]?.id
    }

    const rodSize = lastWellboreSchematic?.rodDiameters || ''

    const rodlength = lastWellboreSchematic?.rodLengths || ''

    const tvd = lastWellboreSchematic?.standingValveDepth || 0

    const cardInfo = dyn['cardData'][0]

    const cardPossition = cardInfo?cardInfo['Surface Card Position']?.join('|'):''
    const cardLoad = cardInfo?cardInfo['Surface Card Load']?.join('|'):''
    const pumpCardPosition = cardInfo?cardInfo['Pump Card Position']?.join('|'):''
    const pumpCardLoad = cardInfo?cardInfo['Pump Card Load']?.join('|'):''

    const analystPayload: any = {
        spm: spm || 0,
        thp: thp || 0,
        api: 10,
        pot: pot || 0,
        ays: 0,
        h2OEspGrav: 1.05,
        pd: dpluger, //pluger diameter
        tvd, //true vertical depth
        rodSize,
        rodlength,
        cardPossition,
        cardLoad,
        pumpCardPosition,
        pumpCardLoad,
    }

    const getAnalystForCard = await requestDataFromAnalyzer(analystPayload)

    if (!ept && getAnalystForCard) {
        const fillageLine = getAnalystForCard?.fillage_line[0]?.x 
        if (fillageLine) {
            ept = typeof fillageLine === 'number' ?fillageLine: parseFloat(fillageLine);
        }
    }

    const diagnostic = previousDiagnostic?.find((v: any) => v.id === "26fd1c80-ec8d-11ef-95e3-00505652cca1")?.value || ""

    const recommendations = previousDiagnostic?.find((v: any) => v.id === "26fd1ece-ec8d-11ef-95e3-00505652cca1")?.value || ""

    const candidateSate = previousDiagnostic?.find((v: any) => v.id === "26fd1f13-ec8d-11ef-95e3-00505652cca1")?.value || ""

    const bottomHoleConditions = previousDiagnostic?.find((v: any) => v.id === "26fd1f4d-ec8d-11ef-95e3-00505652cca1")?.value || ""

    const subBottomHoleConditions = previousDiagnostic?.find((v: any) => v.id === "3e545921-fc61-11ef-a39b-00505652cca1")?.value || ""

    let bbpd = previousDiagnostic?.find((v: any) => v.id === "3e545b5c-fc61-11ef-a39b-00505652cca1")?.value || 0

    if (typeof bbpd !== 'number') {
        bbpd = parseFloat(bbpd);
    }

    let adjustProduction = bbpd * ((pumpEfficiency && typeof pumpEfficiency === 'number' && pumpEfficiency > 100) ? pumpEfficiency / 100 : 1)

    const dyns: Dyn[] = []

    for (const card of cards) {

        try {

            const dataSurface = []

            if (card['Surface Card Load'] && card['Surface Card Position']) {
                for (let index = 0; index < card["Surface Card Load"]?.length; index++) {
                    const position = card["Surface Card Position"]?.[index];
                    const load = card?.['Surface Card Load'][index];
                    dataSurface.push({ x: position, y: load })
                }
            }


            const surfacePeakLoad = Math.max(...dataSurface.map(point => point.y));
            const surfaceMinLoad = Math.min(...dataSurface.map(point => point.y));
            const deltaSurfaceLoad = surfacePeakLoad - surfaceMinLoad;
            const strokeLength = Math.max(...dataSurface.map(point => point.x));

            const dataBottom = []

            if (card["Pump Card Load"] && card["Pump Card Position"]) {
                for (let index = 0; index < card['Pump Card Load']?.length; index++) {
                    const position = card?.['Pump Card Position'][index];
                    const load = card?.['Pump Card Load'][index];
                    dataBottom.push({ x: position, y: load })
                }
            }

            const bottomPeakLoad = Math.max(...dataBottom.map(point => point.y));
            const bottomMinLoad = Math.min(...dataBottom.map(point => point.y));
            const deltaBottomLoad = bottomPeakLoad - bottomMinLoad;
            const mpt = Math.max(...dataBottom.map(point => point.x));

            if (ept > mpt || !ept) {
                ept = mpt
            } else if (ept < 0) {
                ept = 0
            } else {
                ept = ept
            }

            const eptData = [
                { x: ept, y: bottomMinLoad || 0 },
                { x: ept, y: bottomPeakLoad }
            ]

            const surfaceUnitBrand = variables?.find((v: any) => v.variable === "Marca Balancin")?.value

            const surfaceUnitModel = variables?.find((v: any) => v.variable === "Modelo Balancin")?.value

            const structuralMaxLoad = variables?.find((v: any) => v.variable === "structural_max_load")?.value || 0

            const currentPercentageMaxLoad = structuralMaxLoad > 0 ? (surfacePeakLoad / structuralMaxLoad) * 100 : 0

            dyns.push({
                surfacePeakLoad,
                surfaceMinLoad,
                deltaSurfaceLoad,
                strokeLength,
                bottomPeakLoad,
                bottomMinLoad,
                deltaBottomLoad,
                mpt,
                ept: ept,
                datasetSurface: {
                    datasets: [
                        { label: "Carta de Superficie", data: dataSurface, backgroundColor: ["rgba(82, 137, 220, 1)"], showLine: true, borderColor: 'rgba(82, 137, 220, 1)' }
                    ]
                },
                datasetBottom: {
                    datasets: [
                        { label: "Carta de Fondo", data: dataBottom, backgroundColor: ["rgba(82, 137, 220, 1)"], showLine: true, borderColor: 'rgba(82, 137, 220, 1)' },
                        { label: "EPT", data: eptData, backgroundColor: ["rgb(26, 27, 27)"], showLine: true, borderColor: 'rgb(26, 27, 27)' }
                    ]
                },
                surfaceUnitBrand,
                surfaceUnitModel,
                structuralMaxLoad,
                currentPercentageMaxLoad
            })
        } catch (error) {
            console.log({ error })
        }
    }

    const optimizationActivitiesSelected:any = previousDiagnostic?.find((v: any) => v.name === "Optimization info")?.value || []

    return {
        fieldActivityId,
        executedDate,
        dpluger,
        bbpd,
        spm,
        pumpEfficiency,
        adjustProduction,
        variables,
        dyns,
        diagnostic,
        recommendations,
        candidateSate,
        bottomHoleConditions,
        subBottomHoleConditions,
        optimizationActivitiesSelected,
    }
}

export const analyzeGroupOfDyn = async (dynCardList: DynsCard[], wellboreDiagram: any) => {

    const listSortDyns: DynSort[] = []
    let enableCard = 0

    for (const dyn of dynCardList) {
        console.log({dyn})
        const sortDyn = await analyzeAndSortDyn(dyn, wellboreDiagram)
        listSortDyns.push(sortDyn)
        enableCard = enableCard + sortDyn.dyns.length
    }

    return { enableCard, listSortDyns }
}

export const calculateProduction = (dpiston: number, spm: number, fillage: number) => {
    let production = 0

    try {
        production = 0.1166 * dpiston * dpiston * spm * fillage
    } catch (error) {
        console.log({ error })
    } finally {
        return production
    }

}